import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus } from 'lucide-react';

interface ClientTagsProps {
  tags: string[];
  mode: 'read' | 'edit';
  maxVisible?: number;
  onAdd?: (tag: string) => void;
  onRemove?: (tag: string) => void;
}

const MAX_TAG_LEN = 40;

function TagChip({
  tag,
  onRemove,
}: {
  tag: string;
  onRemove?: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1 h-6 px-2 bg-brand-50 text-brand-700 border border-brand-200 rounded-full text-xs font-medium">
      {tag}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="text-brand-500 hover:text-brand-700 -mr-0.5"
          aria-label={`Remove ${tag}`}
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

export function ClientTags({
  tags,
  mode,
  maxVisible = 3,
  onAdd,
  onRemove,
}: ClientTagsProps) {
  const { t } = useTranslation('clients');
  const [expanded, setExpanded] = useState(false);
  const [inputOpen, setInputOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputOpen) inputRef.current?.focus();
  }, [inputOpen]);

  const submit = () => {
    const trimmed = inputValue.trim().slice(0, MAX_TAG_LEN);
    if (trimmed && !tags.includes(trimmed)) {
      onAdd?.(trimmed);
    }
    setInputValue('');
    setInputOpen(false);
  };

  if (mode === 'read') {
    const visible = expanded ? tags : tags.slice(0, maxVisible);
    const hidden = tags.length - visible.length;
    return (
      <div className="flex flex-wrap items-center gap-1">
        {visible.map((tag) => (
          <TagChip key={tag} tag={tag} />
        ))}
        {hidden > 0 && !expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="inline-flex items-center h-6 px-2 bg-surface-100 text-surface-600 border border-surface-200 rounded-full text-xs font-medium hover:bg-surface-200"
          >
            {t('tags.moreCount', { count: hidden })}
          </button>
        )}
      </div>
    );
  }

  // edit mode
  return (
    <div className="flex flex-wrap items-center gap-1">
      {tags.map((tag) => (
        <TagChip key={tag} tag={tag} onRemove={() => onRemove?.(tag)} />
      ))}
      {inputOpen ? (
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit();
            } else if (e.key === 'Escape') {
              setInputValue('');
              setInputOpen(false);
            }
          }}
          onBlur={submit}
          maxLength={MAX_TAG_LEN}
          placeholder={t('tags.addPlaceholder')}
          className="h-6 px-2 border border-surface-300 rounded-full text-xs bg-surface-0 focus:outline-none focus:border-brand-500 w-32"
        />
      ) : (
        <button
          type="button"
          onClick={() => setInputOpen(true)}
          className="inline-flex items-center gap-1 h-6 px-2 bg-surface-50 text-surface-500 border border-dashed border-surface-300 rounded-full text-xs font-medium hover:bg-surface-100 hover:text-surface-700"
        >
          <Plus className="w-3 h-3" />
          {t('tags.addTag')}
        </button>
      )}
    </div>
  );
}
